import React, { useEffect, useState } from 'react'
import { CircularProgress, IconButton, Menu } from '@mui/material'
import { MIDMeterFilterValue } from '../types'
import { useExportChargingSessions } from '../hooks/use-export-charging-sessions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { differenceInDays, endOfDay, isSameDay, startOfDay, subDays } from '@e3dc-react/shell/Libs/date'
import { DateRangeValue } from '@e3dc-react/shell/Elements/DatePicker'
import { makeStyles } from 'tss-react/mui'
import { ChargingSessionExportFormatDto } from '../api/dto/charging-session-export-request.dto'
import { J2Download } from '@e3dc-react/icons'
import useModals from 'src/Hooks/useModals'
import { usePDFTemplates } from '../hooks/use-pdf-templates'
import { ExportSessionsPayload } from '../models/charging-sessions-export.model'
import { TemplateCategory } from '../api/dto/pdf-template-category.dto'
import { ExportMenuOption } from './ExportMenuOption'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { useFetchCountryInfo } from '../../shared'
import { useTypedSelector } from 'src/State/RootReducer'

const useStyles = makeStyles()(theme => ({
  button: {
    color: '#4A4A4A',
    height: '24px',
    width: '24px',
  },
  menu: {
    '&.MuiPaper-root': {
      padding: 0,
      marginTop: theme.spacing(2),
    },
  },
}))

interface ChargingSessionsExportButtonProps {
  installationId: number
  wallboxId: string[]
  ocpiTokenIds: string[]
  midMeter: MIDMeterFilterValue[]
  dateRange: DateRangeValue | undefined
}

/**
 * Export charging sessions button component.
 * It contains export button and menu with menu items, handle function export file from BE
 * @param - installationId as unique installation identifier
 * @param - filters as ChargingSessionTableState object
 */
export const ChargingSessionsExportButton: React.FC<ChargingSessionsExportButtonProps> = ({
  installationId,
  dateRange,
  wallboxId,
  ocpiTokenIds,
  midMeter,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)

  const { t } = useCustomTranslation()
  const installation = useTypedSelector(state => state.storage.installation)

  const { classes } = useStyles()
  const { openNoDateRangeSelectedExportSessionsDialog, openDateRangeInvalidExportSessionDialog } = useModals()

  const { templates, isLoading: isPDFTemplatesLoading } = usePDFTemplates({ installationId })
  const { country } = useFetchCountryInfo(installation?.countryCode ?? '', installationId, Boolean(installation?.decommissionedAt))

  /**
   * For better user experience, we do not disable the export button initially while pdf template fetch is ongoing,
   * but rather "fake" the loading status as false for the first 1000ms since it is nearly impossible to start the
   * export before the pdf template is loaded
   **/
  const [delayedIsPDFTemplatesLoading, setDelayedIsPDFTemplatesLoading] = useState(false)

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = (): void => {
    setAnchorEl(null)
  }

  const { exportSessions, isLoading: exportInProgress } = useExportChargingSessions()
  const isLoading = delayedIsPDFTemplatesLoading || exportInProgress

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDelayedIsPDFTemplatesLoading(isPDFTemplatesLoading)
    }, 1000)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isPDFTemplatesLoading])

  const handleExportFile = (
    exportFormat: ChargingSessionExportFormatDto,
    availableLanguage: AppLang | undefined,
    reimbursementPrice?: number
  ): void => {
    const startDate = startOfDay(new Date(dateRange?.startDate ?? 0)).toISOString()

    let endDate

    if (isSameDay(new Date(), dateRange?.endDate ?? 0)) {
      endDate = new Date().toISOString()
    } else {
      endDate = dateRange?.endDate ? endOfDay(new Date(dateRange.endDate)).toISOString() : new Date().toISOString()
    }

    const payload: ExportSessionsPayload = {
      dateFrom: startDate,
      dateTo: endDate,
      wallboxIdFilter: wallboxId,
      rfidFilter: ocpiTokenIds,
      midMeter: midMeter,
      installationId,
      exportFormat: exportFormat,
      language: availableLanguage ?? 'en',
      reimbursementPrice,
    }

    if (!dateRange?.startDate) {
      const startDateMinus366 = subDays(new Date(), 366)
      payload.dateFrom = startOfDay(startDateMinus366).toISOString()
      openNoDateRangeSelectedExportSessionsDialog({ exportSessions, payload })
    } else if (differenceInDays(new Date(endDate), new Date(startDate)) > 366) {
      openDateRangeInvalidExportSessionDialog()
    } else {
      exportSessions(payload)
    }
  }

  return (
    <>
      <IconButton id="e-mobility-charging-sessions-export-button" onClick={handleOpenMenu} disabled={isLoading}>
        {isLoading ? <CircularProgress size={24} /> : <J2Download className={classes.button} />}
      </IconButton>
      <Menu
        slotProps={{
          paper: {
            className: classes.menu,
          },
        }}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCloseMenu}
      >
        <ExportMenuOption
          templates={templates}
          category={TemplateCategory.CDR_E3DC_XLSX_CSV}
          format={ChargingSessionExportFormatDto.Csv}
          label={t('emobility.chargingSession.export-csv')}
          handleExportFile={handleExportFile}
          handleCloseMenu={handleCloseMenu}
        />

        <ExportMenuOption
          category={TemplateCategory.CDR_E3DC_PDF}
          templates={templates}
          label={t('emobility.chargingSession.export-pdf')}
          format={ChargingSessionExportFormatDto.Pdf}
          handleExportFile={handleExportFile}
          handleCloseMenu={handleCloseMenu}
        />

        <ExportMenuOption
          handleCloseMenu={handleCloseMenu}
          category={TemplateCategory.CDR_E3DC_ENERGY_PDF}
          format={ChargingSessionExportFormatDto.PdfNoCosts}
          handleExportFile={handleExportFile}
          label={t('emobility.chargingSession.export-pdf-energy-only')}
          templates={templates}
        />

        {country?.averageReimbursement && (
          <ExportMenuOption
            handleCloseMenu={handleCloseMenu}
            category={TemplateCategory.CDR_E3DC_PDF_REIMBURSEMENT}
            format={ChargingSessionExportFormatDto.PdfReimbursement}
            handleExportFile={handleExportFile}
            label={t('emobility.chargingSession.export-pdf-reimbursement')}
            templates={templates}
            currency={country?.currencyCode}
          />
        )}

        <ExportMenuOption
          handleExportFile={handleExportFile}
          label={t('emobility.chargingSession.export-excel')}
          format={ChargingSessionExportFormatDto.Xlsx}
          templates={templates}
          handleCloseMenu={handleCloseMenu}
          category={TemplateCategory.CDR_E3DC_XLSX_CSV}
        />
      </Menu>
    </>
  )
}
